import { useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  muscleName,
  toggleSetValue,
  type EquipmentKind,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { TextField } from "../primitives/TextField";
import { FilterChipsRow } from "../ExercisePicker/FilterChipsRow";
import { difficultyLabel } from "./difficultyLabel";
import {
  activeAxisCount,
  type LibraryDifficulty,
  type LibraryFilterOptions,
  type LibraryTagFilters,
} from "./libraryFilters";

// Search + tag-filter panel for the Library browser (HR-S3c). The search
// bar is always visible; the four filter axes (muscles · equipment · level
// · frequency) live behind a compact "Filters ▾" toggle so the browse list
// stays above the fold. Axes are FilterChipsRow instances (the house
// multi-select filter primitive): OR within an axis, AND across axes —
// the compute lives in libraryFilters.ts, this component is chrome only.

// Shared immutable Set toggle (@jackedlogs/shared) — same helper the
// ExercisePicker filters use.
const toggleSet = toggleSetValue;

export type LibraryFiltersPanelProps = {
  theme: UiTheme;
  filters: LibraryTagFilters;
  onChange: (f: LibraryTagFilters) => void;
  options: LibraryFilterOptions;
};

export function LibraryFiltersPanel({
  theme,
  filters,
  onChange,
  options,
}: LibraryFiltersPanelProps) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const styles = makeStyles(theme);
  const [open, setOpen] = useState(false);

  const activeCount = activeAxisCount(filters);
  const musclesSorted = useMemo(
    () =>
      [...options.muscles].sort((a, b) =>
        muscleName(a, lang).localeCompare(muscleName(b, lang)),
      ),
    [options.muscles, lang],
  );
  // FilterChipsRow is generic over string — frequency chips ride as the
  // stringified number, parsed back on toggle.
  const perWeekOptions = useMemo(
    () => options.perWeek.map(String),
    [options.perWeek],
  );
  const perWeekActive = useMemo(
    () => new Set(Array.from(filters.perWeek, String)),
    [filters.perWeek],
  );

  const toggleLabel = t("library.filters", { defaultValue: "Filters" });

  return (
    <View style={styles.wrap}>
      <TextField
        theme={theme}
        value={filters.query}
        onChangeText={(query) => onChange({ ...filters, query })}
        placeholder={t("library.searchPlaceholder", {
          defaultValue: "Search by name…",
        })}
        autoCorrect={false}
        autoCapitalize="none"
      />
      <View style={styles.toggleRow}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: open }}
          onPress={() => setOpen((v) => !v)}
          hitSlop={8}
          style={({ pressed }) => [styles.toggle, pressed && styles.pressed]}
        >
          <Text style={styles.toggleText}>
            {activeCount > 0 ? `${toggleLabel} (${activeCount})` : toggleLabel}
          </Text>
          <Icon
            name={open ? "chevron-up" : "chevron-down"}
            size={theme.typography.sizes.sm}
            color={theme.palette.ink2}
          />
        </Pressable>
        {activeCount > 0 ? (
          <Pressable
            accessibilityRole="button"
            onPress={() =>
              onChange({
                ...filters,
                muscles: new Set(),
                equipment: new Set(),
                levels: new Set(),
                perWeek: new Set(),
              })
            }
            hitSlop={8}
            style={({ pressed }) => [styles.toggle, pressed && styles.pressed]}
          >
            <Text style={styles.clearText}>
              {t("library.clearFilters", { defaultValue: "Clear" })}
            </Text>
          </Pressable>
        ) : null}
      </View>

      {open ? (
        <View style={styles.axes}>
          {musclesSorted.length > 0 ? (
            <FilterChipsRow
              theme={theme}
              label={t("mobile.preview.muscles", { defaultValue: "Muscles" })}
              options={musclesSorted}
              active={filters.muscles}
              onToggle={(m) =>
                onChange({ ...filters, muscles: toggleSet(filters.muscles, m) })
              }
              renderLabel={(m) => muscleName(m, lang)}
              flush
            />
          ) : null}
          {options.equipment.length > 0 ? (
            <FilterChipsRow
              theme={theme}
              label={t("catalog.equipment", { defaultValue: "Equipment" })}
              options={options.equipment}
              active={filters.equipment}
              onToggle={(kind: EquipmentKind) =>
                onChange({
                  ...filters,
                  equipment: toggleSet(filters.equipment, kind),
                })
              }
              renderLabel={(kind) =>
                t(`catalog.equipment_${kind}`, { defaultValue: kind })
              }
              flush
            />
          ) : null}
          {options.levels.length > 0 ? (
            <FilterChipsRow
              theme={theme}
              label={t("library.axisLevel", { defaultValue: "Level" })}
              options={options.levels}
              active={filters.levels}
              onToggle={(d: LibraryDifficulty) =>
                onChange({ ...filters, levels: toggleSet(filters.levels, d) })
              }
              renderLabel={(d) => difficultyLabel(d, t)}
              flush
            />
          ) : null}
          {perWeekOptions.length > 0 ? (
            <FilterChipsRow
              theme={theme}
              label={t("library.axisPerWeek", {
                defaultValue: "Sessions / week",
              })}
              options={perWeekOptions}
              active={perWeekActive}
              onToggle={(v) =>
                onChange({
                  ...filters,
                  perWeek: toggleSet(filters.perWeek, Number(v)),
                })
              }
              renderLabel={(v) =>
                t("program.sessionsPerWeekLabel", {
                  n: Number(v),
                  defaultValue: `${v}× / week`,
                })
              }
              flush
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    wrap: {
      gap: s.s2,
    },
    toggleRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s5,
    },
    toggle: {
      minHeight: 36,
      flexDirection: "row",
      alignItems: "center",
      alignSelf: "flex-start",
      gap: s.s2,
    },
    toggleText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    clearText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
      textDecorationLine: "underline",
    },
    pressed: { opacity: 0.6 },
    axes: {
      gap: s.s2,
    },
  });
}
